import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import { useController, useFormContext } from 'react-hook-form';
import { getError } from '../../../utils/FormError';
import InlineLoader from '../../Loader/InlineLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import LaboratoryLogo from '../../Laboratory/LaboratoryLogo';

import './ImprovedSelect.scss';

const ImprovedSelect = forwardRef(({
  options = [],
  name,
  onInputChange,
  onChange: externalOnChange,
  onLoadMore,
  hasMore = false,
  multiple = false,
  placeholder = 'Select...',
  validationRules = {},
  allowCustomValue = false,
  loading = false,
  className,
  disabled = false,
  noOptionsFooter = null,
  // Typing to filter only makes sense for server-driven search, free-text values, or a long
  // list — a short fixed list (e.g. notification channels) is a plain click-to-pick field.
  // Pass `searchable` explicitly to override.
  searchable,
  // no chevron button on the right: a pure search field — the results only open once something
  // is typed (an empty field lists nothing)
  hideToggle = false,
}, ref) => {
  const isSearchable = searchable ?? (!!onInputChange || allowCustomValue || options.length > 10);
  const { control, formState: { errors } } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(multiple ? [] : null);
  const [filter, setFilter] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const instanceId = useRef(Math.random().toString(36));
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const error = getError(errors, name);

  const {
    field: { onChange, onBlur, value, ref: innerRef },
  } = useController({
    name,
    control,
    rules: validationRules,
    defaultValue: multiple ? [] : '',
  });

  // Close other open selects when this one opens
  useEffect(() => {
    if (isOpen) {
      document.dispatchEvent(new CustomEvent('dropdownOpen', { detail: { id: instanceId.current } }));
    }
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail.id !== instanceId.current) setIsOpen(false);
    };
    document.addEventListener('dropdownOpen', handler);
    return () => document.removeEventListener('dropdownOpen', handler);
  }, []);

  // Scroll infini
  useEffect(() => {
    if (!isOpen) return;
    const el = dropdownRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        if (hasMore && onLoadMore) {
          onLoadMore();
        }
      }
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isOpen, hasMore, onLoadMore]);

  // Click outside
  useEffect(() => {
    const clickOutside = (e) => {
      if (!containerRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', clickOutside, true);
    return () => document.removeEventListener('mousedown', clickOutside, true);
  }, []);

  // Compute dropdown position — opens below by default, but flips above the trigger when
  // there isn't enough room underneath (e.g. a row near the bottom of the viewport/a scroll
  // container) and there's more room above, so the list stays fully visible either way.
  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const isRtl = document.documentElement.dir === 'rtl';
    // In RTL documents the scrollbar can render on the left, so window.innerWidth
    // (which always includes the scrollbar's width) throws off a right-edge
    // calculation — document.documentElement.clientWidth excludes it and matches
    // the coordinate space getBoundingClientRect() already uses.
    const left = isRtl ? document.documentElement.clientWidth - rect.right : rect.left;

    const PREFERRED_HEIGHT = 460;
    const MARGIN = 8;
    // The on-screen keyboard (see VirtualKeyboard) covers the bottom of the viewport while
    // open — treat its top edge as the bottom of the usable space.
    const keyboardHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--vk-height')) || 0;
    const viewportBottom = window.innerHeight - keyboardHeight;
    const spaceBelow = viewportBottom - rect.bottom - MARGIN;
    const spaceAbove = rect.top - MARGIN;
    const openUpward = spaceBelow < PREFERRED_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(PREFERRED_HEIGHT, openUpward ? spaceAbove : spaceBelow));

    setDropdownPos({
      top: openUpward ? undefined : rect.bottom + 2,
      bottom: openUpward ? window.innerHeight - rect.top + 2 : undefined,
      left,
      width: rect.width,
      isRtl,
      maxHeight,
    });
  }, []);

  useEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  // Reposition on scroll or resize while open
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Sync selected avec value
  useEffect(() => {
    if (multiple && Array.isArray(value)) {
      const selectedValues = options.filter((opt) => value.includes(opt.value));
      setSelected(selectedValues);
    } else if (!multiple && value) {
      const found = options.find((opt) => opt.value === value);
      if (found) {
        setSelected(found);
        if (!filter) setFilter(found.label);
      } else if (allowCustomValue) {
        setSelected(null);
        if (!filter) setFilter(value ?? '');
      }
    }
  }, [value, options, multiple]);

  useImperativeHandle(ref, () => ({
    setExternalValue: (extVal) => {
      setSelected(extVal);
      setFilter(extVal.label);
      setIsOpen(false);
      onChange(extVal.value);
      externalOnChange?.(extVal);
    },
  }));

  const toggleOption = (opt) => {
    if (multiple) {
      const updated = selected.some((s) => s.value === opt.value)
        ? selected.filter((s) => s.value !== opt.value)
        : [...selected, opt];
      setSelected(updated);
      onChange(updated.map((i) => i.value));
      setFilter('');
      externalOnChange?.(updated);
    } else {
      setSelected(opt);
      setFilter(opt.label);
      setIsOpen(false);
      onChange(opt.value);
      externalOnChange?.(opt);
    }
  };

  // When search is server-driven (onInputChange provided, e.g. InputProduct/InputPatient),
  // `options` already IS the relevant/filtered set from the backend — re-filtering here by
  // requiring the typed text to be a literal substring of just the label would hide results
  // the server matched on other grounds (a different field, reordered tokens...). Only apply
  // this local filter for plain client-side selects with a small fixed option list.
  const filteredOptions = onInputChange
    ? options
    : options.filter((opt) => opt.label.toLowerCase().includes(filter.toLowerCase()));

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => (prev + 1) % filteredOptions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions.length > 0 && highlightIndex >= 0 && highlightIndex < filteredOptions.length) {
          toggleOption(filteredOptions[highlightIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const renderOption = (opt) => (
    <div className="d-flex align-items-center">
      {opt.icon && <span style={{ marginInlineEnd: '1rem' }}>{opt.icon}</span>}
      <span className="d-flex flex-column" style={{ minWidth: 0, gap: 2 }}>
        <span>{opt.label}</span>
        {opt.subtitle && <span style={{ fontSize: '0.75em', opacity: 0.6 }}>{opt.subtitle}</span>}
        {opt.laboratory && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <LaboratoryLogo id={opt.laboratory.id} name={opt.laboratory.name} size={16} />
            <span style={{ fontSize: '0.75em', opacity: 0.7 }}>{opt.laboratory.name}</span>
          </span>
        )}
      </span>
    </div>
  );

  const hasSelection = multiple ? (Array.isArray(selected) ? selected.length > 0 : false) : !!selected;

  return (
    <div className="position-relative w-100" ref={containerRef}>
      <div className={`input-group improved_select ${disabled ? 'disabled' : ''} ${hideToggle ? 'improved_select--no-toggle' : ''}`}>

        {multiple ? (
          /* ── Multi-select: chips + search input ── */
          <div
            className={`improved_select__multi-container ${error ? 'is-invalid' : ''} ${!isSearchable && !disabled ? 'improved_select--readonly' : ''}`}
            onClick={() => {
              if (disabled) return;
              // not searchable: the field acts as a button, so a second click closes the list
              setIsOpen(prev => (isSearchable ? true : !prev));
              inputRef.current?.focus();
            }}
          >
            {selected.map((s) => (
              <span key={s.value} className="improved_select__chip">
                {s.label}
                <span
                  className="improved_select__chip-remove"
                  onMouseDown={(e) => { e.preventDefault(); toggleOption(s); }}
                  onClick={(e) => e.stopPropagation()}
                >×</span>
              </span>
            ))}
            <input
              type="text"
              autoComplete="off"
              className="improved_select__multi-input"
              placeholder={Array.isArray(selected) && selected.length === 0 ? placeholder : ''}
              value={filter}
              readOnly={!isSearchable}
              onChange={(e) => {
                if (!isSearchable) return;
                setFilter(e.target.value);
                onInputChange?.(e.target.value);
                setIsOpen(true);
                setHighlightIndex(0);
              }}
              onKeyDown={handleKeyDown}
              ref={(el) => { inputRef.current = el; innerRef(el); }}
              onBlur={onBlur}
              disabled={disabled}
            />
          </div>
        ) : (
          /* ── Single select: text input ── */
          <div
            style={{ position: 'relative', flex: '1 1 auto', minWidth: 0 }}
            title={selected ? [selected.label, selected.subtitle].filter(Boolean).join(' — ') : undefined}
          >
            <input
              type="text"
              id={name}
              autoComplete="off"
              className={`form-control ${error ? 'is-invalid' : ''} ${className ?? ''}`}
              placeholder={selected ? '' : placeholder}
              value={filter}
              readOnly={!isSearchable}
              onChange={(e) => {
                if (!isSearchable) return;
                setFilter(e.target.value);
                onInputChange?.(e.target.value);
                // search-only field (hideToggle): results only once something is typed
                setIsOpen(!hideToggle || e.target.value.trim() !== '');
                setHighlightIndex(0);
                if (selected) externalOnChange?.(null);
                setSelected(null);
                onChange('');
              }}
              onKeyDown={handleKeyDown}
              ref={(el) => { inputRef.current = el; innerRef(el); }}
              onBlur={() => {
                if (!allowCustomValue && !selected) {
                  setFilter('');
                  onChange('');
                } else if (allowCustomValue && !selected) {
                  onChange(filter);
                  externalOnChange?.({ value: filter, label: filter });
                }
                onBlur();
              }}
              onClick={() => {
                if (disabled) return;
                // search-only field (hideToggle): a click on an empty field doesn't list everything
                if (hideToggle && filter.trim() === '') return;
                setIsOpen(prev => (isSearchable ? true : !prev));
              }}
              disabled={disabled}
              style={{
                width: '100%', borderTopRightRadius: 0, borderBottomRightRadius: 0,
                ...(!isSearchable && !disabled && { cursor: 'pointer', caretColor: 'transparent' }),
              }}
            />
            {selected?.subtitle && !isOpen && (
              <div
                style={{
                  position: 'absolute', inset: '1px', display: 'flex', alignItems: 'center', gap: 6,
                  padding: '0 0.75rem', background: disabled ? '#e9ecef' : '#fff', pointerEvents: 'none',
                  overflow: 'hidden', whiteSpace: 'nowrap',
                  borderTopLeftRadius: 'calc(0.375rem - 1px)', borderBottomLeftRadius: 'calc(0.375rem - 1px)',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected.label}</span>
                <span style={{ color: '#9ca3af', fontSize: '0.78em', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1 }}>
                  {selected.subtitle}
                </span>
              </div>
            )}
          </div>
        )}

        {hasSelection && !disabled && (
          <div
            className="input-group-text improved_select__clear"
            onMouseDown={(e) => {
              e.preventDefault();
              setSelected(multiple ? [] : null);
              setFilter('');
              onChange(multiple ? [] : '');
              externalOnChange?.(null);
              onInputChange?.('');
              setIsOpen(false);
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </div>
        )}
        {!hideToggle && (
          <div
            className={`input-group-text ${isOpen ? 'open' : ''}`}
            onClick={() => { if (disabled) return; setIsOpen((prev) => !prev); }}
          >
            <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
          </div>
        )}
      </div>

      {isOpen && createPortal(
        <div
          className="dropdown-menu show p-0"
          ref={dropdownRef}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
            ...(dropdownPos.isRtl
              ? { right: dropdownPos.left }
              : { left: dropdownPos.left }),
            width: dropdownPos.width,
            maxHeight: dropdownPos.maxHeight ? `${dropdownPos.maxHeight}px` : '460px',
            overflowY: 'auto',
            zIndex: 9999,
          }}
        >
          <ul className="list-unstyled mb-0">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, i) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    className={`dropdown-item d-flex justify-content-between px-3 py-2 ${highlightIndex === i ? 'bg-primary text-white' : ''
                      }`}
                    onMouseDown={(e) => { e.preventDefault(); toggleOption(opt); }}
                  >
                    {renderOption(opt)}
                    <span style={{ width: '1em', flexShrink: 0, textAlign: 'center' }}>
                      {selected && ((multiple && selected.find((s) => s.value === opt.value)) ||
                        (!multiple && selected.value === opt.value)) && <>&#10003;</>}
                    </span>
                  </button>
                </li>
              ))
            ) : (
              <>
                <li className="dropdown-item text-muted">Aucune option</li>
                {noOptionsFooter && (
                  <li onMouseDown={e => e.preventDefault()}>{noOptionsFooter}</li>
                )}
              </>
            )}
            {loading && (
              <li className="dropdown-item px-3 py-2"><InlineLoader /></li>
            )}
          </ul>
        </div>,
        document.body
      )}

      {error && <div className="invalid-feedback d-block">{error.message}</div>}
    </div>
  );
});

export default ImprovedSelect;
